import { useState } from "react";
import { isMessageSent } from "@/react/components/Chat/ChatWindowV2/helpers/helpers";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";
import {
  useChatRoomStoreActions,
  useChatRoomStoreChatRoom,
  useChatRoomStoreStates,
} from "@circle-react/hooks/chatsV2/store";
import { useRailbar } from "@circle-react/hooks/chatsV2/useRailbar";
import { updateParentMessageOnNewMessage } from "./helper";
import { useMarkChatRoomAsRead } from "./useMarkChatRoomAsRead";
import { useScrollInfo } from "./useScrollInfo";

interface FetchChatMessagesParams {
  id?: number | null;
  isInitialPage?: boolean;
  nextPerPage?: number;
  parentMessageId?: string | null;
  previousPerPage?: number;
}

export const useChatMessagesV2Api = () => {
  const {
    setChatRoomMessages,
    setScrollInfo,
    appendLastMessage,
    deleteMessage,
    updateMessage,
    fetchChatMessages: fetchChatMessagesFromStore,
  } = useChatRoomStoreActions();
  const { isViewOnlyMasquerading } = usePunditUserContext();
  const { scrollInfo, topMessageId } = useChatRoomStoreStates();
  const chatRoom = useChatRoomStoreChatRoom();
  const currentParticipant = chatRoom?.current_participant;
  const [isUnreadMessageFlow, setUnreadMessageFlow] = useState(false);
  const { parentMessage, updateParentMessage } = useRailbar();
  const { markChatRoomAsRead } = useMarkChatRoomAsRead();
  const { calculateScrollInfo, getScrollingElement, targetDocument } =
    useScrollInfo();

  const fetchChatMessages = async (params: FetchChatMessagesParams) => {
    try {
      const data = await fetchChatMessagesFromStore(params);
      const isLastPage = !data.has_next_page;
      if (
        isLastPage &&
        !isUnreadMessageFlow &&
        !isViewOnlyMasquerading &&
        chatRoom?.uuid
      ) {
        void markChatRoomAsRead({ uuid: chatRoom?.uuid });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchChatMessagesFirstPage = async (messageId?: number | null) => {
    try {
      let params: FetchChatMessagesParams = {
        isInitialPage: true,
      };
      if (messageId) {
        params = {
          ...params,
          id: messageId,
          previousPerPage: 20,
          nextPerPage: 20,
        };
      }
      await fetchChatMessages(params);
    } catch (err) {
      console.error(err);
    }
  };

  const adjustScroller = () => {
    const element = getScrollingElement();
    if (!element) return;
    const unreadMessageBreakpoint = targetDocument.getElementById(
      "unread-message-breakpoint",
    );

    switch (scrollInfo.action) {
      case "scrollToBottom":
        element.scrollTop = element.scrollHeight;
        break;
      case "adjustTop":
        if (topMessageId) {
          const messageElement = targetDocument.getElementById(
            topMessageId.toString(),
          );
          messageElement?.scrollIntoView();
        } else {
          const scrollInfoHeight = scrollInfo.scrollHeightValue ?? 0;
          const scrollInfoTop = scrollInfo.scrollTopValue ?? 0;
          element.scrollTop =
            element.scrollHeight - (scrollInfoHeight - scrollInfoTop);
        }
        break;
      case "scrollToUnreadMessageIndicator":
        if (unreadMessageBreakpoint) {
          unreadMessageBreakpoint.scrollIntoView({
            block: "center",
          });
        } else {
          element.scrollTop = element.scrollHeight;
        }
        break;
    }
  };

  const onEventReceive = (eventData: any, chatMessages: any) => {
    switch (eventData.event) {
      case "newMessage":
        onNewMessage(eventData, chatMessages);
        break;
      case "deletedMessage":
        onDeleteMessage(eventData);
        break;
      case "updatedMessage":
        onUpdateMessage(eventData);
        break;
    }
  };

  const onDeleteMessage = (eventData: {
    id: number;
    parent_message?: ChatRoomMessage;
  }) => {
    const { id: messageId, parent_message: parentMessage } = eventData;
    if (parentMessage) {
      updateMessage(parentMessage);
    } else {
      deleteMessage(messageId);
    }
  };

  const onUpdateMessage = (eventData: { json_message: ChatRoomMessage }) => {
    setScrollInfo(calculateScrollInfo({ event: "messageUpdated" }));
    const updatedMessage = eventData.json_message;
    updateMessage(updatedMessage);

    // Update parent message if it is in a reply
    if (updatedMessage.id === parentMessage?.id) {
      updateParentMessage(updatedMessage);
    }
  };

  const onNewMessage = (
    eventData: any,
    chatMessages: ChatRoomMessage[] = [],
  ) => {
    const latestChatRoomMessage: ChatRoomMessage = eventData.json_message;
    const { parent_message_id: parentMessageId } = latestChatRoomMessage;
    const isThreadMessage = !!parentMessageId;

    if (!isThreadMessage) {
      const scrollInfo = calculateScrollInfo({
        event: isMessageSent(latestChatRoomMessage, currentParticipant)
          ? "messageSent"
          : "messageReceived",
      });
      setScrollInfo(scrollInfo);
      appendLastMessage(latestChatRoomMessage);
      return;
    }

    setScrollInfo(calculateScrollInfo({ event: "parentMessageUpdated" }));
    updateParentMessageOnNewMessage({
      chatMessages,
      setChatRoomMessages,
      newMessage: latestChatRoomMessage,
    });
  };

  return {
    fetchChatMessages,
    fetchChatMessagesFirstPage,
    adjustScroller,
    onEventReceive,
    setUnreadMessageFlow,
  };
};
